import {
  blue,
  crimson,
  cyan,
  gold,
  grass,
  gray,
  indigo,
  lime,
  limeDark,
  purple,
  teal,
  tomato,
} from '@radix-ui/colors';

const background = {
  '--background-default': '#F9F9FE',
  '--background-soft': '#FFFFFF',
  '--background-backdrop': 'rgba(242, 243, 253, 0.24)',
  '--background-overlay':
    'linear-gradient(180deg, rgba(249, 249, 254, 1) 0%, rgba(249, 249, 254, 0.85) 100%)',
  '--background-gradient':
    'linear-gradient(180deg, rgba(242, 243, 253, 0.00) 0%, rgba(242, 243, 253, 0.24) 100%)',
};

const surface = {
  '--surface-soft': '#E5E6F0',
  '--surface-default': '#EFF0F7',
  '--surface-hover': '#E1E2EC',
  '--surface-active': '#E1E2EC',
  '--surface-inverse': '#040D26',
  '--surface-primary': '#1A2EF9',
  '--surface-success': '#D9FDD7',
  '--surface-warning': '#FFF2DA',
  '--surface-critical': '#FFF0EF',
  '--surface-neutral': '#F2F3FD',
  '--surface-white': '#FFFFFF',

  '--surface-decorative-indigo-default': indigo.indigo3,
  '--surface-decorative-indigo-hover': indigo.indigo4,
  '--surface-decorative-indigo-active': indigo.indigo5,

  '--surface-decorative-teal-default': teal.teal3,
  '--surface-decorative-teal-hover': teal.teal4,
  '--surface-decorative-teal-active': teal.teal5,

  '--surface-decorative-cyan-default': cyan.cyan3,
  '--surface-decorative-cyan-hover': cyan.cyan4,
  '--surface-decorative-cyan-active': cyan.cyan5,

  '--surface-decorative-purple-default': purple.purple3,
  '--surface-decorative-purple-hover': purple.purple4,
  '--surface-decorative-purple-active': purple.purple5,

  '--surface-decorative-lime-default': lime.lime3,
  '--surface-decorative-lime-hover': lime.lime4,
  '--surface-decorative-lime-active': lime.lime5,

  '--surface-decorative-gold-default': gold.gold3,
  '--surface-decorative-gold-hover': gold.gold4,
  '--surface-decorative-gold-active': gold.gold5,
};

const text = {
  '--text-default': '#040D26',
  '--text-soft': '#283044',
  '--text-disabled': '#B9C0DA',
  '--text-inverse': '#FFFFFF',
  '--text-primary': '#1A2EF9',
  '--text-success': '#006327',
  '--text-warning': '#695200',
  '--text-critical': '#AD0028',
  '--text-on-primary': '#FFFFFF',
  '--text-on-critical': '#FFFFFF',
  '--text-link': '#040D26',

  '--text-decorative-indigo': indigo.indigo11,
  '--text-decorative-teal': teal.teal11,
  '--text-decorative-cyan': cyan.cyan11,
  '--text-decorative-purple': purple.purple11,
  '--text-decorative-lime': lime.lime11,
  '--text-decorative-gold': gold.gold10,
};

const icon = {
  '--icon-default': '#040D26',
  '--icon-soft': '#283044',
  '--icon-disabled': '#B9C0DA',
  '--icon-inverse': '#FFFFFF',
  '--icon-primary': '#1A2EF9',
  '--icon-success': '#006327',
  '--icon-warning': '#695200',
  '--icon-critical': '#AD0028',
  '--icon-on-primary': '#FFFFFF',
  '--icon-on-critical': '#FFFFFF',
  '--icon-decorative-indigo': indigo.indigo11,
  '--icon-decorative-teal': teal.teal11,
  '--icon-decorative-cyan': cyan.cyan11,
  '--icon-decorative-purple': purple.purple11,
  '--icon-decorative-lime': lime.lime10,
  '--icon-decorative-crimson': crimson.crimson9,
  '--icon-success-lime': limeDark.lime9,
  '--icon-decorative-blue': '#3E63DD',
  '--icon-decorative-gold': gold.gold10,
};

const border = {
  '--border-default': '#D7DAE7',
  '--border-soft': '#E5E6F0',
  '--border-hover': '#C3C7D9',
  '--border-disabled': 'transparent',
  '--border-primary': '#1A2EF9',
  '--border-success': '#006327',
  '--border-warning': '#FBD364',
  '--border-critical': '#AD0028',
  '--border-focus': '#1A2EF9',
  '--border-decorative-indigo': indigo.indigo9,
  '--border-decorative-teal': teal.teal9,
  '--border-decorative-cyan': cyan.cyan9,
  '--border-decorative-purple': purple.purple9,
  '--border-decorative-lime': lime.lime9,
  '--border-decorative-gold': gold.gold9,
};

const action = {
  '--action-primary-default': '#1A2EF9',
  '--action-primary-hover': '#0018D5',
  '--action-primary-disabled': '#E6E7F5',
  '--action-primary-soft-hover': '#E5E5FF',
  '--action-critical-default': '#AD0028',
  '--action-critical-hover': '#8A001E',
  '--action-critical-disabled': '#E6E7F5',
  '--action-critical-soft-hover': '#FFE1E0',
};

const visual = {
  '--viz-bullish': '#006327',
  '--viz-bullish-soft': '#D9FDD7',
  '--viz-bearish': '#AD0028',
  '--viz-bearish-soft': '#FFF0EF',
  '--viz-neutral-soft': '#F2F3FD',
};

const elevation = {
  '--elevation-subtle':
    '0px 4px 16px -4px rgba(0, 0, 0, 0.12), 0px 0px 6px -3px rgba(0, 0, 0, 0.08)',
  '--elevation-moderate':
    '0px 8px 20px -4px rgba(0, 0, 0, 0.12), 0px 3px 6px -3px rgba(0, 0, 0, 0.08)',
  '--elevation-strong':
    '0px 8px 20px 1px rgba(0, 0, 0, 0.12), 0px 3px 6px 1px rgba(0, 0, 0, 0.08)',
};

const custom = {
  '--background-default-rgb': '252, 252, 252',
  '--background-soft-rgb': '249, 249, 249',
  '--background-table-zebra': gray.gray2,
  '--chart-pane-gradient': gray.gray3,
  '--chart-multi-lines-hex-1': '#7E8CFF',
  '--chart-multi-lines-hex-2': '#FF6334',
  '--chart-multi-lines-hex-3': '#BB75FF',
  '--chart-multi-lines-hex-4': '#00A996',
  '--chart-multi-lines-hex-5': '#D47C8D',
  '--chart-multi-lines-hex-6': '#BC8F00',
  '--chart-multi-lines-hex-7': '#00A996',
  '--chart-multi-lines-hex-8': '#F65EA5',
};

const kg = {
  '--kg-theme': indigo.indigo11,
  '--kg-parent': indigo.indigo9,
  '--kg-brand': teal.teal11,
};

const light_default = {
  ...background,
  ...surface,
  ...border,
  ...text,
  ...icon,
  ...action,
  ...visual,
  ...elevation,
  ...custom,
  ...kg,
};

const light_inverted = {
  ...light_default,
  '--viz-bullish': tomato.tomato11,
  '--viz-bullish-soft': tomato.tomato4,
  '--viz-bearish': grass.grass11,
  '--viz-bearish-soft': grass.grass4,
};
const light_asian = {
  ...light_default,
  '--viz-bullish': tomato.tomato11,
  '--viz-bullish-soft': tomato.tomato4,
  '--viz-bearish': blue.blue11,
  '--viz-bearish-soft': blue.blue4,
};

const light_protanopia = {
  ...light_default,
  '--viz-bullish': grass.grass11,
  '--viz-bullish-soft': grass.grass4,
  '--viz-bearish': blue.blue11,
  '--viz-bearish-soft': blue.blue4,
};

const light_deuteranopia = {
  ...light_default,
  '--viz-bullish': blue.blue11,
  '--viz-bullish-soft': blue.blue4,
  '--viz-bearish': tomato.tomato11,
  '--viz-bearish-soft': tomato.tomato4,
};

export const lightTheme = {
  light_default,
  light_inverted,
  light_asian,
  light_protanopia,
  light_deuteranopia,
};
